import { darken } from 'polished';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Images } from '../../../../@resources/img';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { useAppEvent } from '../../../../CodesThatsGottaGoSomewhereElse';
import { Colors, toRem } from '../../../../ui';
import { Shadows } from '../../../../ui/Standards';
import { useResolve } from '../../../Container';
import { Copy, Flexer } from '../../elements/LayoutElements';

const Hover = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2rem);
    background: rgba(0, 0, 0, 0.05);
    border-radius: 1rem;
    overflow: hidden;
`;

const HoverButton = styled.div`
    flex: 1 1 auto;
    padding: .5rem 1rem;
    color: ${Colors.text};
    font-weight: 450;
    font-size: 3rem;
    text-align: center;
    border-radius: .25rem;
    user-select: none;
    cursor: pointer;

    &:hover {
        background: ${Colors.white};
    }
    &:active {
        background: #fefefe;
    }
`;

const Image = styled.div<{ src?: string }>`
    position: relative;
    overflow: hidden;

    ${p => p.src && css`
        background-image: url(${p.src});
        background-size: cover;
        background-position: center;    
    `}

    img { 
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const Info = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: .5rem;
    padding: 1rem;
`;

const Header = styled.div`
    flex: 0 0 auto;
    position: relative;
    display: flex;
    align-items: start;
    justify-content: space-between;
`;

const Label = styled.div`
    flex: 1 1 auto;
    padding-right: 2.25rem;
    color: ${Colors.text};
    font-size: 1.125rem;
    font-weight: 450;    
`;

const ActionMenu = styled.div`
    flex: 0 0 auto;
`;

const Tag = styled.div<{ color: string }>`
    display: block;
    padding: .125rem .375rem;
    background: white;
    border-radius: 0.25rem;
    border: solid .125rem ${p => p.color};
    color: ${p => p.color};
    font-size: 0.875rem;
    font-weight: 500;
    text-align: center;
    opacity: .75;
`;

const TagList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
`;


const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: .5rem;
    position: relative;
    flex: 1 1 auto;
    position: relative;
    color: ${Colors.textLight};

    ${Copy} {
        margin: 0;  
        color: ${Colors.textLight};
    }
`;

const Footer = styled.div`
    flex: 0 0 auto;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${Colors.textLighter};
    font-size: .875rem;
`;

const Author = styled.div`
    
`;

const StarButton = styled.div<{ active?: boolean }>`
    color: ${p => p.active ? Colors.yellow500 : Colors.textLighter};
`;

const Wrapper = styled.div`
    position: relative;

    ${ActionMenu} {
        position: absolute;
        top: 1.25rem;
        right: 1rem;
    }

    ${StarButton} {
        position: absolute;
        bottom: 1rem;
        right: 1rem;
    }
`;

export const useTemplateThumb = ({ id, thumbUrl }: { id?: string, thumbUrl?: string } = {}) => {

    const api = useResolve(ApiClient);
    const [url, setUrl] = useState(thumbUrl);

    const fetchUrl = useCallback(async () => {
        if (!id) return;
        const resp = await api.exec(v2.templates.thumb(id));
        setUrl(resp.url);
    }, [id]);

    useEffect(() => {
        if (!url) fetchUrl();
    }, [id]);

    useAppEvent('template:thumb', useCallback(({ templateId }) => {
        if (templateId === id) {
            fetchUrl();
        }
    }, [id]));

    return url;
};

const Thumb = React.memo<{ id?: string, thumbUrl?: string }>((props) => {
    const thumbUrl = useTemplateThumb(props);
    return <>
        {!!thumbUrl && <img src={thumbUrl} />}
        {!thumbUrl &&
            <Flexer flex center inset background={Colors.borderLight}>
                <Flexer width="4" height="4">
                    <img src={Images.LogoOutline} />
                </Flexer>
            </Flexer>
        }
    </>;
});

const Base = styled.div<{ clickable?: boolean, selected?: boolean, width?: string | number, height?: string | number, layout?: string }>`
    display: grid;
    grid-template-rows: ${p => p.layout || '55% 45%'};
    width: ${p => toRem(p.width || 24)};
    height: ${p => toRem(p.height || 32)};
    border: solid .125rem #DDE2EC;
    border-radius: .75rem;
    background: white;
    overflow: hidden;
    transition: box-shadow .15s ease-out;
    user-select: none;

    ${p => p.clickable && css`
        cursor: pointer;
        &:hover {
            border: solid .125rem ${Colors.textLightest};
            box-shadow: ${Shadows.md};
            transition: none;
            ${Hover} {
                opacity: 1;
            }
        }
        &:active {
            border: solid .125rem ${darken(.05, Colors.textLightest)};
            background: #fcfcfc;
        }
    `}

    ${p => p.selected && css`
        border: solid .125rem ${Colors.primaryLight} !important;
        box-shadow: 0 0px 0 .125rem ${Colors.primaryLight} !important;
    `};
        
`;

export const TemplateCard = Object.assign(Base, {
    Wrapper,
    Image,
    Thumb,
    Hover,
    HoverButton,
    Info,
    Header,
    Label,
    ActionMenu,
    Tag,
    TagList,
    Body,
    Footer,
    Author,
    StarButton,
});
